import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  fetchProjectDashboard,
  saveProjectProfile,
  saveProjectFunding,
  saveProjectMilestone,
  deleteProjectMilestone,
  completeProjectMilestone,
  activateNextProjectMilestone,
  recordProjectDonation,
  saveProjectUpdate,
  deleteProjectUpdate,
} from "./contentStudioApi";
import RecipientPayoutSettings from "../Workspace/RecipientPayoutSettings";
import "./ContentStudio.css";
import "./ProjectStudio.css";

const emptyMilestone = {
  id: "",
  title: "",
  description: "",
  status: "upcoming",
  display_order: 100,
  target_date: "",
  recipient_name: "",
  goal_amount: "",
  amount_raised: "",
  funding_ends_at: "",
  image_path: "",
  impact_summary: "",
  notes: "",
};

const emptyDonation = {
  amount: "",
  donor_name: "",
  note: "",
  source: "manual",
};

function responseBody(err) {
  return (err && err.response && err.response.data) || null;
}

function failureMessage(body) {
  if (body && body.error === "forbidden") {
    return "You don't have permission to make this change.";
  }
  const msg = body && (body.error_messages || body.error);
  return typeof msg === "string" && msg ? msg : "Save failed. Try again.";
}

const emptyUpdate = {
  id: "",
  title: "",
  update_type: "news",
  body: "",
  media_url: "",
  is_published: true,
};

function ProjectStudioPage(props) {
  const embedded = !!props.embedded;
  const projectId =
    props.projectId ||
    (props.match && props.match.params && props.match.params.projectId) ||
    (typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("project_id")) ||
    "";
  const focusSection = props.section || null;

  const [dash, setDash] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");
  const [notice, setNotice] = useState(null);
  const [profileForm, setProfileForm] = useState({});
  const [fundingForm, setFundingForm] = useState({});
  const [milestoneForm, setMilestoneForm] = useState(emptyMilestone);
  const [donationForm, setDonationForm] = useState(emptyDonation);
  const [updateForm, setUpdateForm] = useState(emptyUpdate);

  const applyDash = (body) => {
    if (!body || !body.success) {
      setError((body && (body.error || body.error_messages)) || "Unable to load project studio");
      setDash(null);
      return;
    }
    setError("");
    setDash(body);
    const p = body.project || {};
    setProfileForm({
      name: p.name || "",
      slug: p.slug || "",
      category: p.category || "",
      phase: p.phase || "",
      project_type: p.project_type || "one_time",
      status: p.status || "draft",
      visibility: p.visibility || "public",
      summary: p.summary || "",
      description: p.description || "",
      mission: p.mission || "",
      objectives: p.objectives || "",
      timeline: p.timeline || "",
      leader_name: p.leader_name || "",
      leader_title: p.leader_title || "",
      logo_path: p.logo_path || "",
      cover_image_path: p.cover_image_path || "",
      promo_video_url: p.promo_video_url || "",
      website: p.website || "",
      donation_url: p.donation_url || "",
      is_featured: !!p.is_featured,
      is_verified: !!p.is_verified,
    });
    setFundingForm({
      goal_amount: p.goal_amount != null ? p.goal_amount : "",
      amount_raised: p.amount_raised != null ? p.amount_raised : 0,
      currency: p.currency || "USD",
      funding_ends_at: p.funding_ends_at
        ? String(p.funding_ends_at).slice(0, 10)
        : "",
      donation_mode: p.donation_mode === "external" ? "external" : "internal",
      donation_url: p.donation_url || "",
      progress_percent: p.progress_percent != null ? p.progress_percent : 0,
    });
  };

  const load = async () => {
    if (!projectId) {
      setError("Pass project id via /studio/project/:projectId");
      return;
    }
    setError("");
    let body = null;
    try {
      body = await fetchProjectDashboard(projectId);
    } catch (err) {
      body = responseBody(err) || { success: false };
    }
    applyDash(body);
  };

  useEffect(() => {
    load();
  }, [projectId]);

  // Every mutation clears its busy state and reports success or the server error;
  // a failed save keeps the current dashboard and form values.
  const runAction = async (key, section, request, successText, onSuccess) => {
    setSaving(key);
    setNotice(null);
    let body = null;
    try {
      body = await request();
    } catch (err) {
      body = responseBody(err);
    }
    setSaving("");
    if (body && body.success) {
      applyDash(body);
      setNotice({ section, kind: "success", text: successText });
      if (onSuccess) onSuccess();
      return;
    }
    setNotice({ section, kind: "error", text: failureMessage(body) });
  };

  const renderNotice = (section) =>
    notice && notice.section === section ? (
      <p
        className={notice.kind === "error" ? "cstudio-error" : "cstudio-muted"}
        role={notice.kind === "error" ? "alert" : "status"}
      >
        {notice.text}
      </p>
    ) : null;

  const onSaveProfile = (e) => {
    e.preventDefault();
    return runAction(
      "profile",
      "profile",
      () =>
        saveProjectProfile(
          Object.assign({}, profileForm, {
            project_id: projectId,
            is_featured: profileForm.is_featured ? 1 : 0,
            is_verified: profileForm.is_verified ? 1 : 0,
          })
        ),
      "Profile saved."
    );
  };

  const onSaveFunding = (e) => {
    e.preventDefault();
    return runAction(
      "funding",
      "funding",
      () => saveProjectFunding(Object.assign({}, fundingForm, { project_id: projectId })),
      "Funding saved."
    );
  };

  const onSaveMilestone = (e) => {
    e.preventDefault();
    // "id" is reserved for the authenticated user on userApi requests.
    const payload = Object.assign({}, milestoneForm, {
      project_id: projectId,
    });
    if (payload.id) {
      payload.milestone_id = payload.id;
    }
    delete payload.id;
    return runAction(
      "milestone",
      "milestones",
      () => saveProjectMilestone(payload),
      "Milestone saved.",
      () => setMilestoneForm(emptyMilestone)
    );
  };

  const onEditMilestone = (item) => {
    setMilestoneForm({
      id: item.id || "",
      title: item.title || "",
      description: item.description || "",
      status: item.status || "upcoming",
      display_order: item.display_order != null ? item.display_order : 100,
      target_date: item.target_date || "",
      recipient_name: item.recipient_name || "",
      goal_amount: item.goal_amount != null ? item.goal_amount : "",
      amount_raised: item.amount_raised != null ? item.amount_raised : "",
      funding_ends_at: item.funding_ends_at
        ? String(item.funding_ends_at).slice(0, 10)
        : "",
      image_path: item.image_path || "",
      impact_summary: item.impact_summary || "",
      notes: item.notes || "",
    });
  };

  const onCompleteMilestone = (id) =>
    runAction(
      "milestone-complete",
      "milestones",
      () =>
        completeProjectMilestone({
          project_id: projectId,
          milestone_id: id,
          publish_feed: 1,
          auto_advance: 1,
        }),
      "Milestone completed."
    );

  const onActivateNext = () =>
    runAction(
      "milestone-activate",
      "milestones",
      () => activateNextProjectMilestone({ project_id: projectId }),
      "Next milestone activated."
    );

  const onRecordDonation = (e) => {
    e.preventDefault();
    return runAction(
      "donation",
      "milestones",
      () => recordProjectDonation(Object.assign({}, donationForm, { project_id: projectId })),
      "Donation recorded.",
      () => setDonationForm(emptyDonation)
    );
  };

  const onDeleteMilestone = (id) =>
    runAction(
      "milestone-del",
      "milestones",
      () => deleteProjectMilestone({ project_id: projectId, milestone_id: id }),
      "Milestone deleted."
    );

  const onSaveUpdate = (e) => {
    e.preventDefault();
    const payload = Object.assign({}, updateForm, {
      project_id: projectId,
      is_published: updateForm.is_published ? 1 : 0,
    });
    if (payload.id) {
      payload.update_id = payload.id;
    }
    delete payload.id;
    return runAction(
      "update",
      "updates",
      () => saveProjectUpdate(payload),
      "Announcement saved.",
      () => setUpdateForm(emptyUpdate)
    );
  };

  const onEditUpdate = (item) => {
    setUpdateForm({
      id: item.id || "",
      title: item.title || "",
      update_type: item.update_type || "news",
      body: item.body || "",
      media_url: item.media_url || "",
      is_published: item.is_published !== false,
    });
  };

  const onDeleteUpdate = (id) =>
    runAction(
      "update-del",
      "updates",
      () => deleteProjectUpdate({ project_id: projectId, update_id: id }),
      "Announcement removed.",
      () => {
        if (String(updateForm.id) === String(id)) setUpdateForm(emptyUpdate);
      }
    );

  // Server-issued project role capabilities; absent means read-only.
  const perms = (dash && dash.permissions) || {};
  const canEditProject = !!perms.can_edit_project;
  const canManageFunding = !!perms.can_manage_funding;
  const canManageMilestones = !!perms.can_manage_milestones;
  const canRecordDonation = !!perms.can_record_donation;
  const canAddUpdate = !!perms.can_add_update;

  const project = (dash && dash.project) || {};
  const stats = (dash && dash.stats) || {};
  const catalogs = (dash && dash.catalogs) || {};
  const milestones = (dash && dash.milestones) || [];
  const updates = (dash && dash.updates) || [];

  useEffect(() => {
    if (!focusSection || typeof document === "undefined") return;
    const el = document.getElementById(focusSection);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [focusSection, dash]);

  return (
    <div className={"cstudio project-studio" + (embedded ? " cstudio--embedded" : "")}>
      {!embedded ? (
        <>
          <Helmet>
            <title>Project Manager · TPG</title>
          </Helmet>
          <header className="cstudio__header">
            <div>
              <p className="cstudio-eyebrow">Project Manager Dashboard</p>
              <h1>{project.name || "Project"}</h1>
              {project.public_path ? (
                <p className="project-studio__public">
                  Public page:{" "}
                  <Link to={project.public_path}>{project.public_path}</Link>
                </p>
              ) : null}
            </div>
            <nav className="cstudio__nav">
              <Link to="/community/projects">Community Projects</Link>
              <Link to="/ops">Livestreams</Link>
            </nav>
          </header>
        </>
      ) : null}

      {error ? <p className="cstudio-error">{error}</p> : null}

      <div className="cstudio-grid">
        <div className="cstudio-stat">
          <strong>{stats.funding_percent || 0}%</strong>
          <span>Funded</span>
        </div>
        <div className="cstudio-stat">
          <strong>{stats.progress_percent || 0}%</strong>
          <span>Progress</span>
        </div>
        <div className="cstudio-stat">
          <strong>
            {stats.milestone_completed || 0}/{stats.milestone_total || 0}
          </strong>
          <span>Milestones</span>
        </div>
        <div className="cstudio-stat">
          <strong>{stats.updates || 0}</strong>
          <span>Updates</span>
        </div>
        <div className="cstudio-stat">
          <strong>
            {stats.days_remaining != null ? stats.days_remaining : "—"}
          </strong>
          <span>Days left</span>
        </div>
      </div>

      <section id="profile" className="cstudio-panel project-studio__panel">
        <h2>Profile & visibility</h2>
        <form onSubmit={onSaveProfile}>
          <fieldset className="project-studio__form project-studio__fieldset" disabled={!canEditProject}>
          <label>
            Name
            <input
              value={profileForm.name || ""}
              onChange={(e) =>
                setProfileForm(Object.assign({}, profileForm, { name: e.target.value }))
              }
              required
            />
          </label>
          <label>
            Slug
            <input
              value={profileForm.slug || ""}
              onChange={(e) =>
                setProfileForm(Object.assign({}, profileForm, { slug: e.target.value }))
              }
            />
          </label>
          <label>
            Category
            <input
              value={profileForm.category || ""}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { category: e.target.value })
                )
              }
            />
          </label>
          <label>
            Phase
            <input
              value={profileForm.phase || ""}
              onChange={(e) =>
                setProfileForm(Object.assign({}, profileForm, { phase: e.target.value }))
              }
            />
          </label>
          <label>
            Project type
            <select
              value={profileForm.project_type || "one_time"}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { project_type: e.target.value })
                )
              }
            >
              {Object.keys(
                catalogs.project_types || {
                  one_time: "One-Time",
                  ongoing: "Ongoing",
                }
              ).map((key) => (
                <option key={key} value={key}>
                  {(catalogs.project_types && catalogs.project_types[key]) || key}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={profileForm.status || "draft"}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { status: e.target.value })
                )
              }
            >
              {Object.keys(catalogs.statuses || { draft: "Draft", active: "Active" }).map(
                (key) => (
                  <option key={key} value={key}>
                    {(catalogs.statuses && catalogs.statuses[key]) || key}
                  </option>
                )
              )}
            </select>
          </label>
          <label>
            Visibility
            <select
              value={profileForm.visibility || "public"}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { visibility: e.target.value })
                )
              }
            >
              {Object.keys(
                catalogs.visibilities || { public: "Public", private: "Private" }
              ).map((key) => (
                <option key={key} value={key}>
                  {(catalogs.visibilities && catalogs.visibilities[key]) || key}
                </option>
              ))}
            </select>
          </label>
          <label className="project-studio__full">
            Mission
            <textarea
              rows={3}
              value={profileForm.mission || ""}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { mission: e.target.value })
                )
              }
            />
          </label>
          <label className="project-studio__full">
            Summary
            <textarea
              rows={2}
              value={profileForm.summary || ""}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { summary: e.target.value })
                )
              }
            />
          </label>
          <label className="project-studio__full">
            Description (HTML allowed)
            <textarea
              rows={4}
              value={profileForm.description || ""}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { description: e.target.value })
                )
              }
            />
          </label>
          <label>
            Leader name
            <input
              value={profileForm.leader_name || ""}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { leader_name: e.target.value })
                )
              }
            />
          </label>
          <label>
            Leader title
            <input
              value={profileForm.leader_title || ""}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { leader_title: e.target.value })
                )
              }
            />
          </label>
          <label>
            Logo path
            <input
              value={profileForm.logo_path || ""}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { logo_path: e.target.value })
                )
              }
            />
          </label>
          <label>
            Cover image path
            <input
              value={profileForm.cover_image_path || ""}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, {
                    cover_image_path: e.target.value,
                  })
                )
              }
            />
          </label>
          <label>
            Website
            <input
              value={profileForm.website || ""}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, { website: e.target.value })
                )
              }
            />
          </label>
          <label>
            Promo video URL
            <input
              value={profileForm.promo_video_url || ""}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, {
                    promo_video_url: e.target.value,
                  })
                )
              }
            />
          </label>
          <label className="project-studio__check">
            <input
              type="checkbox"
              checked={!!profileForm.is_featured}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, {
                    is_featured: e.target.checked,
                  })
                )
              }
            />
            Featured
          </label>
          <label className="project-studio__check">
            <input
              type="checkbox"
              checked={!!profileForm.is_verified}
              onChange={(e) =>
                setProfileForm(
                  Object.assign({}, profileForm, {
                    is_verified: e.target.checked,
                  })
                )
              }
            />
            Verified
          </label>
          {canEditProject ? (
            <div className="project-studio__actions project-studio__full">
              <button type="submit" className="btn btn-danger" disabled={saving === "profile"}>
                {saving === "profile" ? "Saving…" : "Save profile"}
              </button>
            </div>
          ) : null}
          </fieldset>
        </form>
        {renderNotice("profile")}
      </section>

      <section id="funding" className="cstudio-panel project-studio__panel">
        <h2>Funding</h2>
        <form onSubmit={onSaveFunding}>
          <fieldset className="project-studio__form project-studio__fieldset" disabled={!canManageFunding}>
          <label>
            Goal amount
            <input
              type="number"
              min="0"
              step="0.01"
              value={fundingForm.goal_amount}
              onChange={(e) =>
                setFundingForm(
                  Object.assign({}, fundingForm, { goal_amount: e.target.value })
                )
              }
            />
          </label>
          <label>
            Amount raised
            <input
              type="number"
              min="0"
              step="0.01"
              value={fundingForm.amount_raised}
              onChange={(e) =>
                setFundingForm(
                  Object.assign({}, fundingForm, {
                    amount_raised: e.target.value,
                  })
                )
              }
            />
          </label>
          <label>
            Currency
            <input
              value={fundingForm.currency || "USD"}
              onChange={(e) =>
                setFundingForm(
                  Object.assign({}, fundingForm, { currency: e.target.value })
                )
              }
            />
          </label>
          <label>
            Ends on
            <input
              type="date"
              value={fundingForm.funding_ends_at || ""}
              onChange={(e) =>
                setFundingForm(
                  Object.assign({}, fundingForm, {
                    funding_ends_at: e.target.value,
                  })
                )
              }
            />
          </label>
          <label>
            Donation mode
            <select
              value={fundingForm.donation_mode || "internal"}
              onChange={(e) =>
                setFundingForm(
                  Object.assign({}, fundingForm, {
                    donation_mode: e.target.value,
                  })
                )
              }
            >
              <option value="internal">Pentecost+ Internal Donation Checkout</option>
              <option value="external">External Donation Campaign URL</option>
            </select>
          </label>
          <label>
            External donation URL
            <input
              value={fundingForm.donation_url || ""}
              disabled={fundingForm.donation_mode === "internal"}
              onChange={(e) =>
                setFundingForm(
                  Object.assign({}, fundingForm, { donation_url: e.target.value })
                )
              }
            />
          </label>
          <label>
            Manual progress %
            <input
              type="number"
              min="0"
              max="100"
              value={fundingForm.progress_percent}
              onChange={(e) =>
                setFundingForm(
                  Object.assign({}, fundingForm, {
                    progress_percent: e.target.value,
                  })
                )
              }
            />
          </label>
          {canManageFunding ? (
            <div className="project-studio__actions project-studio__full">
              <button type="submit" className="btn btn-danger" disabled={saving === "funding"}>
                {saving === "funding" ? "Saving…" : "Save funding"}
              </button>
            </div>
          ) : null}
          </fieldset>
        </form>
        {renderNotice("funding")}
      </section>

      {projectId && perms.can_manage_payout ? (
        <RecipientPayoutSettings
          payableType="project"
          payableId={projectId}
          workspace="project"
          title="Project payout settings"
        />
      ) : null}

      <section id="milestones" className="cstudio-panel project-studio__panel">
        <h2>Milestones</h2>
        <p className="project-studio__hint">
          {(project.project_type || profileForm.project_type) === "ongoing"
            ? "Ongoing projects use milestone campaigns. Completing the active milestone publishes a feed update and activates the next upcoming goal."
            : "One-time projects use milestones as a progress tracker. Switch project type to Ongoing for campaign auto-advance."}
        </p>
        {canManageMilestones ? (
          <div className="project-studio__row-actions" style={{ marginBottom: "0.75rem" }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={onActivateNext}
              disabled={saving === "milestone-activate"}
            >
              {saving === "milestone-activate" ? "Activating…" : "Activate next upcoming"}
            </button>
          </div>
        ) : null}
        {renderNotice("milestones")}
        <ul className="project-studio__list">
          {milestones.map((item) => (
            <li key={item.id}>
              <div>
                <strong>{item.title}</strong>
                <span>
                  {item.status}
                  {item.recipient_name ? ` · ${item.recipient_name}` : ""}
                  {item.target_date ? ` · ${item.target_date}` : ""}
                  {item.goal_amount != null
                    ? ` · ${item.amount_raised || 0}/${item.goal_amount}`
                    : ""}
                </span>
              </div>
              {canManageMilestones ? (
              <div className="project-studio__row-actions">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onEditMilestone(item)}>
                  Edit
                </button>
                {item.status !== "completed" ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={() => onCompleteMilestone(item.id)}
                    disabled={saving === "milestone-complete"}
                  >
                    Complete
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => onDeleteMilestone(item.id)}
                  disabled={saving === "milestone-del"}
                >
                  Delete
                </button>
              </div>
              ) : null}
            </li>
          ))}
        </ul>
        {canManageMilestones ? (
        <form onSubmit={onSaveMilestone} className="project-studio__form">
          <label>
            Title
            <input
              value={milestoneForm.title || ""}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, { title: e.target.value })
                )
              }
              required
            />
          </label>
          <label>
            Status
            <select
              value={milestoneForm.status || "upcoming"}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, { status: e.target.value })
                )
              }
            >
              {Object.keys(
                catalogs.milestone_statuses || {
                  upcoming: "Upcoming",
                  current: "Current",
                  completed: "Completed",
                }
              ).map((key) => (
                <option key={key} value={key}>
                  {(catalogs.milestone_statuses &&
                    catalogs.milestone_statuses[key]) ||
                    key}
                </option>
              ))}
            </select>
          </label>
          <label>
            Order
            <input
              type="number"
              value={milestoneForm.display_order}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, {
                    display_order: e.target.value,
                  })
                )
              }
            />
          </label>
          <label>
            Target date
            <input
              type="date"
              value={milestoneForm.target_date || ""}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, {
                    target_date: e.target.value,
                  })
                )
              }
            />
          </label>
          <label>
            Recipient
            <input
              value={milestoneForm.recipient_name || ""}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, {
                    recipient_name: e.target.value,
                  })
                )
              }
              placeholder="Family / partner / cause"
            />
          </label>
          <label>
            Goal amount
            <input
              type="number"
              step="0.01"
              value={milestoneForm.goal_amount}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, {
                    goal_amount: e.target.value,
                  })
                )
              }
            />
          </label>
          <label>
            Amount raised
            <input
              type="number"
              step="0.01"
              value={milestoneForm.amount_raised}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, {
                    amount_raised: e.target.value,
                  })
                )
              }
            />
          </label>
          <label>
            Campaign ends
            <input
              type="date"
              value={milestoneForm.funding_ends_at || ""}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, {
                    funding_ends_at: e.target.value,
                  })
                )
              }
            />
          </label>
          <label>
            Image path
            <input
              value={milestoneForm.image_path || ""}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, {
                    image_path: e.target.value,
                  })
                )
              }
            />
          </label>
          <label className="project-studio__full">
            Description
            <textarea
              rows={2}
              value={milestoneForm.description || ""}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, {
                    description: e.target.value,
                  })
                )
              }
            />
          </label>
          <label className="project-studio__full">
            Impact summary
            <textarea
              rows={2}
              value={milestoneForm.impact_summary || ""}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, {
                    impact_summary: e.target.value,
                  })
                )
              }
            />
          </label>
          <label className="project-studio__full">
            Notes
            <textarea
              rows={2}
              value={milestoneForm.notes || ""}
              onChange={(e) =>
                setMilestoneForm(
                  Object.assign({}, milestoneForm, { notes: e.target.value })
                )
              }
            />
          </label>
          <div className="project-studio__actions project-studio__full">
            <button type="submit" className="btn btn-danger" disabled={saving === "milestone"}>
              {milestoneForm.id
                ? saving === "milestone"
                  ? "Saving…"
                  : "Update milestone"
                : saving === "milestone"
                ? "Saving…"
                : "Add milestone"}
            </button>
            {milestoneForm.id ? (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setMilestoneForm(emptyMilestone)}
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>
        ) : null}

        {canRecordDonation ? (
        <>
        <h3 className="project-studio__subhead">Record offline donation</h3>
        <p className="project-studio__hint">
          Applies to the active milestone on ongoing projects (or the project total for one-time).
        </p>
        <form onSubmit={onRecordDonation} className="project-studio__form">
          <label>
            Amount
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={donationForm.amount}
              onChange={(e) =>
                setDonationForm(
                  Object.assign({}, donationForm, { amount: e.target.value })
                )
              }
              required
            />
          </label>
          <label>
            Donor name
            <input
              value={donationForm.donor_name || ""}
              onChange={(e) =>
                setDonationForm(
                  Object.assign({}, donationForm, { donor_name: e.target.value })
                )
              }
            />
          </label>
          <label>
            Source
            <select
              value={donationForm.source || "manual"}
              onChange={(e) =>
                setDonationForm(
                  Object.assign({}, donationForm, { source: e.target.value })
                )
              }
            >
              <option value="manual">Manual</option>
              <option value="offline">Offline</option>
              <option value="checkout">Checkout</option>
            </select>
          </label>
          <label className="project-studio__full">
            Note
            <input
              value={donationForm.note || ""}
              onChange={(e) =>
                setDonationForm(
                  Object.assign({}, donationForm, { note: e.target.value })
                )
              }
            />
          </label>
          <div className="project-studio__actions project-studio__full">
            <button type="submit" className="btn btn-danger" disabled={saving === "donation"}>
              {saving === "donation" ? "Recording…" : "Record donation"}
            </button>
          </div>
        </form>
        </>
        ) : null}
      </section>

      <section id="updates" className="cstudio-panel project-studio__panel">
        <h2>Announcements / updates</h2>
        <ul className="project-studio__list">
          {updates.map((item) => (
            <li key={item.id}>
              <div>
                <strong>{item.title}</strong>
                <span>
                  {item.update_type}
                  {item.is_published ? " · published" : " · draft"}
                </span>
              </div>
              {item.can_edit || item.can_delete ? (
              <div className="project-studio__row-actions">
                {item.can_edit ? (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onEditUpdate(item)}>
                  Edit
                </button>
                ) : null}
                {item.can_delete ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => onDeleteUpdate(item.id)}
                  disabled={saving === "update-del"}
                >
                  Delete
                </button>
                ) : null}
              </div>
              ) : null}
            </li>
          ))}
        </ul>
        {renderNotice("updates")}
        {canAddUpdate ? (
        <form onSubmit={onSaveUpdate} className="project-studio__form">
          <label>
            Title
            <input
              value={updateForm.title || ""}
              onChange={(e) =>
                setUpdateForm(
                  Object.assign({}, updateForm, { title: e.target.value })
                )
              }
              required
            />
          </label>
          <label>
            Type
            <input
              value={updateForm.update_type || "news"}
              onChange={(e) =>
                setUpdateForm(
                  Object.assign({}, updateForm, { update_type: e.target.value })
                )
              }
            />
          </label>
          <label className="project-studio__full">
            Body
            <textarea
              rows={3}
              value={updateForm.body || ""}
              onChange={(e) =>
                setUpdateForm(
                  Object.assign({}, updateForm, { body: e.target.value })
                )
              }
            />
          </label>
          <label>
            Media URL
            <input
              value={updateForm.media_url || ""}
              onChange={(e) =>
                setUpdateForm(
                  Object.assign({}, updateForm, { media_url: e.target.value })
                )
              }
            />
          </label>
          <label className="project-studio__check">
            <input
              type="checkbox"
              checked={!!updateForm.is_published}
              onChange={(e) =>
                setUpdateForm(
                  Object.assign({}, updateForm, {
                    is_published: e.target.checked,
                  })
                )
              }
            />
            Published
          </label>
          <div className="project-studio__actions project-studio__full">
            <button type="submit" className="btn btn-danger" disabled={saving === "update"}>
              {updateForm.id
                ? saving === "update"
                  ? "Saving…"
                  : "Update announcement"
                : saving === "update"
                ? "Saving…"
                : "Add announcement"}
            </button>
            {updateForm.id ? (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setUpdateForm(emptyUpdate)}
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>
        ) : null}
      </section>

      <section id="analytics" className="cstudio-panel project-studio__panel">
        <h2>Basic analytics</h2>
        {!embedded ? (
          <p className="project-studio__analytics-note">
            Foundation metrics for raised amount, donors, milestone completion, and
            lifetime impact. Deeper engagement analytics (views, shares, volunteer
            marketplace) remain deferred.
          </p>
        ) : null}
        <dl className="project-studio__analytics">
          <div>
            <dt>Raised</dt>
            <dd>
              {stats.amount_raised != null ? stats.amount_raised : 0} /{" "}
              {stats.goal_amount != null ? stats.goal_amount : 0}
            </dd>
          </div>
          <div>
            <dt>Funding %</dt>
            <dd>{stats.funding_percent || 0}%</dd>
          </div>
          <div>
            <dt>Donors</dt>
            <dd>{stats.donors || 0}</dd>
          </div>
          <div>
            <dt>Avg donation</dt>
            <dd>{stats.average_donation != null ? stats.average_donation : "—"}</dd>
          </div>
          <div>
            <dt>Milestones done</dt>
            <dd>
              {stats.milestone_completed || 0}/{stats.milestone_total || 0}
            </dd>
          </div>
          <div>
            <dt>Lifetime impact</dt>
            <dd>
              {(stats.lifetime_impact && stats.lifetime_impact.amount_raised) ||
                stats.amount_raised ||
                0}{" "}
              raised ·{" "}
              {(stats.lifetime_impact &&
                stats.lifetime_impact.milestones_completed) ||
                stats.milestone_completed ||
                0}{" "}
              completed
            </dd>
          </div>
          <div>
            <dt>Progress %</dt>
            <dd>{stats.progress_percent || 0}%</dd>
          </div>
          <div>
            <dt>Updates</dt>
            <dd>{stats.updates || 0}</dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

export default ProjectStudioPage;
